import React from 'react';
import MuiAvatarGroup, {type AvatarGroupProps as MuiAvatarGroupProps} from '@mui/material/AvatarGroup';
import {useDataProps, useAriaProps} from '@famly/mf_ts-utils_data-aria-props';

import {AvatarGroupContext} from './avatar-group.context';
import {getFontSize} from './avatar-get-font-size';

export type AvatarGroupProps = {
    /**
     * The Avatars to display. Use the Avatar component from Modern Famly.
     *
     * Example:
     * ```tsx
     * return (
     *    <AvatarGroup>
     *        <Avatar initials="AB" />
     *        <Avatar initials="CD" />
     *        <Avatar initials="EF" />
     *        <Avatar initials="GH" />
     *    </AvatarGroup>
     * )
     * ```
     */
    children?: MuiAvatarGroupProps['children'];

    /**
     * The maximum number of Avatars to display before showing an overflow avatar.
     *
     * Defaults to 4.
     */
    max?: MuiAvatarGroupProps['max'];

    /**
     * The size in pixels of the avatar. The font size will be adapted accordingly.
     *
     * Defaults to 32.
     */
    size?: number;
} & DataProps;

const OVERFLOW_CLASSNAME = 'MFAvatarGroup-overflow';
const DEFAULT_MAX = 4;
const DEFAULT_SIZE = 32;

export const AvatarGroup = ({children, max = DEFAULT_MAX, size = DEFAULT_SIZE, ...props}: AvatarGroupProps) => {
    const dataProps = useDataProps(props);
    const ariaProps = useAriaProps(props);

    return (
        <AvatarGroupContext.Provider value={React.useMemo(() => ({size}), [size])}>
            <MuiAvatarGroup
                max={max}
                slotProps={{
                    additionalAvatar: {className: OVERFLOW_CLASSNAME},
                }}
                sx={theme => ({
                    [`& .${OVERFLOW_CLASSNAME}`]: {
                        backgroundColor: theme.modernFamlyTheme.colorPalette.b400,
                        width: `${size}px`,
                        height: `${size}px`,
                        fontSize: `${getFontSize(size)}px`,
                        lineHeight: 'unset',
                    },
                    // Ensures that we start a new stacking context for the z-index applied to reverse the stacking order.
                    position: 'relative',
                })}
                {...dataProps}
                {...ariaProps}
            >
                {children}
            </MuiAvatarGroup>
        </AvatarGroupContext.Provider>
    );
};
